import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Send, Mic, Square, Loader2, ImagePlus } from 'lucide-react';
import clsx from 'clsx';

export interface ChatInputHandle {
  /** Focus the textarea (used by quick-action chips to focus after prefilling). */
  focus: () => void;
}

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  disabled?: boolean;
  placeholder?: string;
  // Voice input (optional — omit to render no mic button)
  voiceSupported?: boolean;
  voiceState?: 'idle' | 'recording' | 'transcribing';
  micLevel?: number;
  onVoiceStart?: () => void;
  onVoiceStop?: () => void;
  // Photo scan-to-fill (optional — omit onPickPhoto to render no attach button)
  onPickPhoto?: (file: File) => void;
  photoState?: 'idle' | 'extracting';
}

const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(({
  value,
  onChange,
  onSend,
  disabled = false,
  placeholder = 'Ask about an error code or unit…',
  voiceSupported = false,
  voiceState = 'idle',
  micLevel = 0,
  onVoiceStart,
  onVoiceStop,
  onPickPhoto,
  photoState = 'idle',
}, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // Expose focus() so a quick-action chip can focus the input after prefilling it.
  useImperativeHandle(ref, () => ({
    focus: () => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      // Put the caret at the end so the tech keeps typing where the scaffold left off.
      const end = el.value.length;
      el.setSelectionRange(end, end);
    },
  }), []);

  // Auto-grow textarea up to 4 lines
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const maxHeight = 4 * 24; // ~4 lines at 24px line height
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
  }, [value]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !disabled) {
        onSend();
      }
    }
  };

  const canSend = value.trim().length > 0 && !disabled;

  return (
    <div className="flex-shrink-0 px-3 pb-4 pt-2">
      {/* One rounded pill holds the textarea, the dictation mic, and send. */}
      <div className="flex items-end gap-2 rounded-3xl border border-air-navy/15 bg-white px-2.5 py-2 shadow-sm
                      focus-within:border-air-navy/40 focus-within:shadow
                      dark:border-gray-700 dark:bg-gray-800">
        {onPickPhoto && (
          <>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              // No `capture`: let each OS show its native chooser (Take Photo OR
              // Photo Library / Files) so a tech can use a saved photo too. A
              // library image flows through the identical extract pipeline.
              data-testid="photo-file-input"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                // No-op while the composer is busy (defense-in-depth; the
                // button is already disabled). Codex P1-6.
                if (file && !disabled) onPickPhoto(file);
                e.target.value = ''; // allow re-selecting the same photo
              }}
            />
            <button
              type="button"
              onClick={() => photoInputRef.current?.click()}
              // Disable while extracting AND while the chat is busy/streaming
              // (parent `disabled`) — no attaching mid-response. Codex P1-6.
              disabled={disabled || photoState === 'extracting'}
              aria-label="Attach a photo"
              className={clsx(
                'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-air-navy',
                'active:scale-95',
                photoState === 'extracting'
                  ? 'bg-gray-100 text-gray-500 cursor-wait dark:bg-gray-700'
                  : 'text-air-navy hover:bg-air-navy/10 disabled:opacity-40 disabled:cursor-not-allowed dark:text-air-skytext dark:hover:bg-white/10',
              )}
            >
              {photoState === 'extracting' ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <ImagePlus className="h-5 w-5" />
              )}
            </button>
          </>
        )}

        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          // text-base = 16px: iOS Safari auto-zooms (and shifts the layout, pushing the
          // send button off-screen) on focus for any input under 16px. 16px prevents it.
          className="min-w-0 flex-1 resize-none border-0 bg-transparent px-2 py-2 text-base text-gray-900 placeholder-gray-500
                     focus:outline-none focus:ring-0 disabled:opacity-50
                     dark:text-gray-100 dark:placeholder-gray-400"
        />

        {voiceSupported && (
          <button
            type="button"
            onClick={voiceState === 'recording' ? onVoiceStop : onVoiceStart}
            disabled={disabled && voiceState === 'idle'}
            aria-label={voiceState === 'recording' ? 'Stop recording' : 'Start voice input'}
            // While recording, the focus ring doubles as a live audio level meter:
            // its outer glow scales with micLevel (0..1). Driven by box-shadow via
            // inline style so the button footprint never changes.
            style={
              voiceState === 'recording'
                ? { boxShadow: `0 0 0 ${2 + Math.round(micLevel * 8)}px rgba(220,38,38,0.30)` }
                : undefined
            }
            className={clsx(
              'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-red-500',
              'active:scale-95',
              voiceState === 'recording'
                ? 'bg-red-600 text-white animate-pulse'
                : voiceState === 'transcribing'
                ? 'bg-gray-100 text-gray-500 cursor-wait dark:bg-gray-700'
                : 'text-air-navy hover:bg-air-navy/10 disabled:opacity-40 disabled:cursor-not-allowed dark:text-air-skytext dark:hover:bg-white/10',
            )}
          >
            {voiceState === 'transcribing' ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : voiceState === 'recording' ? (
              <Square className="h-5 w-5" fill="currentColor" />
            ) : (
              <Mic className="h-5 w-5" />
            )}
          </button>
        )}

        <button
          onClick={onSend}
          disabled={!canSend}
          aria-label="Send message"
          className={clsx(
            'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full transition-all',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-air-navy',
            canSend
              ? 'bg-air-navy text-white hover:bg-air-navy/90 active:scale-95'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500',
          )}
        >
          <Send className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;
